import { Playground } from '../../../components/playground/Playground';
import { PlaygroundColors } from '../../../components/playground/PlaygroundColors';
import { PlaygroundClasses } from '../../../components/playground/PlaygroundClasses';
import { Headline } from '../../../components/Headline';

import { getRandomIcon } from '../../../utils/icons';
import { Note } from '../../../components/Note';

export const Labels = () => (
    <div>
        <Headline anchor='labels'>Labels</Headline>
        <Playground
            example={labels}
            classes={labelsClasses}
            availableColors={
                <PlaygroundColors bgDefault bgPrimary bgSecondary bgInfo bgSuccess bgWarning bgDanger bgMuted />
            }
        />
        <Note>For having icons in labels, the text must be wrapped with a span to ensure the correct spacing.</Note>
        <Note>
            Avoid <code>label-muted</code> on <code>bg-lighter</code> surfaces, including the default ApplicationLayout
            body background. Use a label with stronger contrast or place the muted label on a contrasting nested surface
            such as <code>bg-white</code>.
        </Note>
        <Note>
            Always combine a color modifier with the base class, for example <code>label label-primary</code>. Add
            <code> label-filled</code> for the filled variant.
        </Note>
    </div>
);

export const labelColorMatrix = [
    { label: 'Default', standard: 'label label-default', filled: 'label label-default label-filled' },
    { label: 'Primary', standard: 'label label-primary', filled: 'label label-primary label-filled' },
    { label: 'Secondary', standard: 'label label-secondary', filled: 'label label-secondary label-filled' },
    { label: 'Info', standard: 'label label-info', filled: 'label label-info label-filled' },
    { label: 'Success', standard: 'label label-success', filled: 'label label-success label-filled' },
    { label: 'Warning', standard: 'label label-warning', filled: 'label label-warning label-filled' },
    { label: 'Danger', standard: 'label label-danger', filled: 'label label-danger label-filled' },
    { label: 'Muted', standard: 'label label-muted', filled: 'label label-muted label-filled' },
] as const;

const eachColor = labelColorMatrix.map(({ label }) => label.toLowerCase());

export const labels = (
    <>
        <p className='margin-bottom-5 margin-top-0'>
            Labels are small, colored indicators used to represent statuses, categories, or highlights in the UI.
        </p>
        <ul className='space-y-5 margin-bottom-20'>
            <li>
                The <b>standard labels</b> have a light background and are ideal for subtle status indications that
                blend naturally with the interface.
            </li>
            <li>
                The <b>filled labels</b> use a bold background color, making them more prominent—best suited for drawing
                attention to critical statuses or highlighting important information.
            </li>
        </ul>
        <div className='row'>
            <div className='col-lg-6 margin-bottom-25'>
                <h5>Standard labels</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {labelColorMatrix.map(({ label, standard }) => (
                        <div className={standard} key={label}>
                            {label}
                        </div>
                    ))}
                </div>
            </div>

            <div className='col-lg-6 margin-bottom-25'>
                <h5>Filled labels</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {labelColorMatrix.map(({ label, filled }) => (
                        <div className={filled} key={label}>
                            {label}
                        </div>
                    ))}
                </div>
            </div>

            <div className='col-lg-6 margin-bottom-25'>
                <h5>Labels width icons</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {eachColor.map((color, index) => (
                        <div className={`label label-${color}`} key={index}>
                            <span className={`uikit-icon ${getRandomIcon()}`} />
                            <span>{color}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className='col-lg-6 margin-bottom-25'>
                <h5>Filled labels width icons</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {eachColor.map((color, index) => (
                        <div className={`label label-${color} label-filled`} key={index}>
                            <span>{color}</span>
                            <span className={`uikit-icon ${getRandomIcon()}`} />
                        </div>
                    ))}
                </div>
            </div>

            <div className='col-lg-6 margin-bottom-25'>
                <h5>Condensed labels</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {eachColor.map((color, index) => (
                        <div className={`label label-${color} label-condensed`} key={index}>
                            {color}
                        </div>
                    ))}
                </div>
            </div>

            <div className='col-lg-6 margin-bottom-25'>
                <h5>Condensed and filled labels</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {eachColor.map((color, index) => (
                        <div className={`label label-${color} label-filled label-condensed`} key={index}>
                            {color}
                        </div>
                    ))}
                </div>
            </div>

            <div className='col-lg-12 margin-bottom-25'>
                <h5>Condensed labels width border style</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {eachColor.map((color, index) => (
                        <div className={`label label-${color} label-condensed border-style-dashed`} key={index}>
                            {color}
                        </div>
                    ))}
                </div>
            </div>

            <div className='col-lg-12 margin-bottom-25'>
                <h5>Condensed labels width border style, no background</h5>
                <div className='display-flex flex-wrap gap-10'>
                    {eachColor.map((color, index) => (
                        <div className={`label label-${color} label-condensed border-style-dashed bg-none`} key={index}>
                            {color}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </>
);

const labelsClasses = (
    <>
        <PlaygroundClasses label='Colors' prefix='label-' classes={eachColor} />
        <PlaygroundClasses label='Variants' prefix='label-' classes={['filled', 'condensed']} />
    </>
);
